import { isAdsEditor } from "../../utils";
import { PanelType } from "../constants/app-options";
import useAppContext from "../hooks/useAppContext";
import useSetIsSidebarOpen from "../hooks/useSetIsSidebarOpen";
import { ILayer, LayerType } from "../types";
import { OBJECT_TYPES } from "../views/DesignEditor/components/ContextMenu/ContextMenusData";
import { ObjectsEnum } from "../views/DesignEditor/components/Panels/panelItems";

export type AdsEditorUtils = {
	openTemplatesTab: () => void;
	openBrandsTab: () => void;
	getFabricLayerTakenConfigs: (layers: any[], type: LayerType, isLogo?: boolean) => Set<string>;
	getFirstAvailableConfig: (taken: Set<string>, type: LayerType, baseLabel?: string) => string | null;
	isStaticText: (item: ILayer) => boolean;
	isOuterRectangleItem: (item: fabric.Object) => boolean;
	isStaticVector: (item: ILayer) => boolean;
	isFabricShape: (item: ILayer) => boolean;
	isStaticImage: (item: ILayer) => boolean;
	isLogoImage: (item: ILayer) => boolean;
	isInnerRectangleItem: (item: fabric.Object) => boolean;
	isSmartImageItem: (item: fabric.Object) => boolean;
};
export const logoConfigLabel = "Logo";
const UseAdsEditorUtils = (): AdsEditorUtils => {
	const { setActivePanel } = useAppContext();
	const setIsSidebarOpen = useSetIsSidebarOpen();

	const openTemplatesTab = async () => {
		if (!isAdsEditor()) {
			setIsSidebarOpen(true);
			setActivePanel(PanelType.TEMPLATES);
		}
	};

	const openBrandsTab = async () => {
		if (!isAdsEditor()) {
			setIsSidebarOpen(true);
			setActivePanel(PanelType.BRANDS);
		}
	};

	const MAX_CONFIG_SLOTS = 5;

	const CONFIG_KEYS: Partial<Record<LayerType, { key: string; match: string[] }>> = {
		[LayerType.STATIC_IMAGE]: { key: "imageType", match: ["image"] },
		[LayerType.STATIC_VECTOR]: { key: "shapeType", match: ["shape"] },
		[LayerType.STATIC_RECT]: { key: "shapeType", match: ["basic shape"] },
		[LayerType.STATIC_CIRCLE]: { key: "shapeType", match: ["basic shape"] },
		[LayerType.STATIC_TRIANGLE]: { key: "shapeType", match: ["basic shape"] },
		[LayerType.STATIC_TEXT]: { key: "textType", match: ["heading", "body"] },
	};

	const CONFIG_LABELS: Partial<Record<LayerType, string[]>> = {
		[LayerType.STATIC_IMAGE]: ["Image"],
		[LayerType.STATIC_VECTOR]: ["Shape"],
		[LayerType.STATIC_RECT]: ["Basic Shape"],
		[LayerType.STATIC_CIRCLE]: ["Basic Shape"],
		[LayerType.STATIC_TRIANGLE]: ["Basic Shape"],
		[LayerType.STATIC_TEXT]: ["Heading", "Body"],
	};

	const getFabricLayerTakenConfigs = (layers: any[], type: LayerType, isLogo = false): Set<string> => {
		const taken = new Set<string>();
		const configKey = isLogo ? "logoConfigType" : CONFIG_KEYS[type]?.key;
		const match = isLogo ? ["logo"] : CONFIG_KEYS[type]?.match;

		if (!configKey || !match) return taken;

		for (const layer of layers) {
			const objects = layer.objects || [layer];
			for (const obj of objects) {
				const value = obj?.[configKey]?.toLowerCase?.();
				if (value && match.some((m) => value.startsWith(m))) {
					taken.add(obj[configKey]);
				}
			}
		}

		return taken;
	};

	const getFirstAvailableConfig = (taken: Set<string>, type: LayerType, baseLabel?: string): string | null => {
		const baseLabels = baseLabel ? [baseLabel] : CONFIG_LABELS[type];
		if (!baseLabels) return null;

		for (const label of baseLabels) {
			for (let i = 1; i <= MAX_CONFIG_SLOTS; i++) {
				const candidate = `${label} #${i}`;
				if (!taken.has(candidate)) return candidate;
			}
		}

		return null;
	};

	const isStaticText = (item: ILayer) =>
		item?.name === OBJECT_TYPES.STATIC_TEXT || item?.type === OBJECT_TYPES.STATIC_TEXT;

	const isStaticImage = (item: ILayer) =>
		item?.name === OBJECT_TYPES.STATIC_IMAGE || item?.type === OBJECT_TYPES.STATIC_IMAGE;
	const isLogoImage = (item: ILayer): boolean =>
		!!item.isLogo && (item.name === OBJECT_TYPES.STATIC_IMAGE || item.type === OBJECT_TYPES.STATIC_IMAGE);

	const isFabricShape = (item: ILayer) =>
		item?.type === LayerType.STATIC_RECT ||
		item?.type === LayerType.STATIC_CIRCLE ||
		item?.type === LayerType.STATIC_TRIANGLE;

	const isStaticVector = (item: ILayer) => item?.type === LayerType.STATIC_VECTOR;

	const isSmartImageItem = (item: fabric.Object) =>
		item?.name === OBJECT_TYPES.GROUP &&
		Array.isArray(item._objects) &&
		item._objects.length >= 2 &&
		item._objects.some((obj: any) => obj.id === ObjectsEnum.SmartImage);

	const isInnerRectangleItem = (item: fabric.Object) =>
		item?.id === ObjectsEnum.InnerRectangle ||
		(item?.name === OBJECT_TYPES.GROUP &&
			Array.isArray(item._objects) &&
			item._objects.length >= 2 &&
			item._objects.some((obj: any) => obj.id === ObjectsEnum.InnerRectangle));

	const isOuterRectangleItem = (item: fabric.Object) =>
		item?.id === ObjectsEnum.OuterRectangle ||
		(item?.name === OBJECT_TYPES.GROUP &&
			Array.isArray(item._objects) &&
			item._objects.length >= 2 &&
			item._objects.some((obj: any) => obj.id === ObjectsEnum.OuterRectangle));

	return {
		openTemplatesTab,
		openBrandsTab,
		getFabricLayerTakenConfigs,
		getFirstAvailableConfig,
		isOuterRectangleItem,
		isInnerRectangleItem,
		isSmartImageItem,
		isStaticVector,
		isFabricShape,
		isStaticImage,
		isStaticText,
		isLogoImage,
	};
};

export default UseAdsEditorUtils;
